import {
  createAsyncThunk,
  createSlice,
  current,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../app/store";
import {
  BusinessStatus,
  BusinessType,
  IBusinessFull,
  IBusinessFullQuery,
  IPosRTConfig,
} from "../../interfaces/businessFull.interface";
import { IBusinessSimple } from "../../interfaces/businessSimple.interface";
import { IBusinessUser } from "../../interfaces/businessUser.interface";
import {
  IQueryFilters,
  ISortParams,
} from "../../interfaces/queryFilters.interface";
import { getUniqueKey } from "../../utils/getUniqueKey";
import {
  type UpdateBusinessPricingPayload,
  approveBusinessServer,
  completeRegistrationExternalOnboardingServer,
  completeRegistrationServer,
  deactivateBusinessServer,
  declineBusinessServer,
  deleteAndArchiveBusinessServer,
  editBusinessServer,
  editBusinessUsersEmailServer,
  editBusinessUserServer,
  getAdyenDocumentStateFromServer,
  getBusinessAdditionalFilesFromServer,
  getBusinessDetailsFromServer,
  getFiscalizationConfigurationFromServer,
  getBusinessesFromServer,
  getBusinessOnboardingFromServer,
  getBusinessUsersFromServer,
  markBusinessAsPendingVerificationServer,
  revertSubmissionBusinessServer,
} from "./businessesApi";
import { IBusinessFile } from "../../interfaces/businessFile.interface";
import {
  AdyenDocumentStateRow,
  describeAdyenDocumentReason,
} from "../../interfaces/adyenDocumentState.interface";
import {
  extractAdyenDocumentReason,
  isAdyenDocumentResponseCurrent,
  isAdyenDocumentStateUnavailable,
  shouldFetchAdyenDocumentState,
} from "./components/Adyen-Documents/adyenDocumentState.helpers";
import { IOnboarding } from "../../interfaces/onboarding.interface";

// import { fetchCount } from './counterAPI';

export interface BusinessesState {
  businesses: IBusinessFull[];
  totalCount: number;
  businessesInTable: IBusinessFull[];
  currentBusiness: IBusinessFull;
  currentBusinessFiles: IBusinessFile[];
  currentBusinessOnboarding: IOnboarding;
  currentBusinessUsers: IBusinessUser[];
  currentBusinessUser: IBusinessUser;
  businessTableStatus: "all" | BusinessStatus;
  businessTableType: "all" | BusinessType;
  lastUpdateKey: string;
  filters: Partial<IBusinessFullQuery>;
  isLoadingBusinessFiles: boolean;
  isLoadingBusinessOnboarding: boolean;
  isLoadingBusinessUsers: boolean;
  isLoadingBusinesses: boolean;
  posRTConfigs: IPosRTConfig[];
  isLoadingPosRTConfigs: boolean;
  currentBusinessAdyenDocuments: AdyenDocumentStateRow[];
  isLoadingAdyenDocuments: boolean;
  // Kept so a failed fetch reads as unknown rather than "Adyen has nothing".
  adyenDocumentsError: string | null;
  // Which business the rows describe, so two screens share one fetch.
  adyenDocumentsBusinessId: string | null;
  // Latest fetch id, so a slower older response for the same business cannot win.
  adyenDocumentsRequestId: string | null;
}

const initialState: BusinessesState = {
  businesses: [],
  totalCount: 0,
  businessesInTable: [],
  businessTableStatus: "all",
  businessTableType: "all",
  currentBusiness: {} as any,
  currentBusinessOnboarding: {} as any,
  currentBusinessFiles: [] as any,
  currentBusinessUsers: [] as any,
  currentBusinessUser: {} as any,
  lastUpdateKey: "",
  filters: {},
  isLoadingBusinessFiles: false,
  isLoadingBusinessOnboarding: false,
  isLoadingBusinessUsers: false,
  isLoadingBusinesses: false,
  posRTConfigs: [],
  isLoadingPosRTConfigs: false,
  currentBusinessAdyenDocuments: [],
  isLoadingAdyenDocuments: false,
  adyenDocumentsError: null,
  adyenDocumentsBusinessId: null,
  adyenDocumentsRequestId: null,
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(getBusinesses(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const getBusinessesThunk = createAsyncThunk(
  "businesses/getAll",
  async (params: IQueryFilters<IBusinessFullQuery> & ISortParams) => {
    const { data, totalCount, sortableFields } =
      await getBusinessesFromServer(params);
    return { data, totalCount, sortableFields };
  }
);

export const getBusinessThunk = createAsyncThunk(
  "businesses/getBusiness",
  async (businessId: string) => {
    const response = await getBusinessDetailsFromServer(businessId);

    return response;
  }
);

export const getFiscalizationConfigurationThunk = createAsyncThunk(
  "businesses/getFiscalizationConfiguration",
  async (params?: { country?: string }) => {
    const response = await getFiscalizationConfigurationFromServer(params);
    return response.posRTConfigs ?? [];
  }
);
// `silent` re-signs the links in place; it never reaches the API.
export const getBusinessFilesThunk = createAsyncThunk(
  "businesses/files",
  async ({
    silent: _silent,
    ...params
  }: {
    email: string;
    businessRegistrationNumber: string;
    businessName: string;
    iban: string;
    name: string;
    silent?: boolean;
  }) => {
    const response = await getBusinessAdditionalFilesFromServer(params);
    return response;
  }
);
// Rejects with the operator-facing reason, or null when the endpoint simply is
// not answering — nothing worth putting on screen for that.
export const getAdyenDocumentStateThunk = createAsyncThunk<
  AdyenDocumentStateRow[],
  { businessId: string; force?: boolean },
  { rejectValue: string | null }
>(
  "businesses/adyenDocuments",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getAdyenDocumentStateFromServer({
        businessId: params.businessId,
      });
      return response.rows ?? [];
    } catch (error) {
      const reason = extractAdyenDocumentReason(error);
      if (isAdyenDocumentStateUnavailable(reason)) {
        console.warn("Adyen document state endpoint is not answering", error);
        return rejectWithValue(null);
      }
      return rejectWithValue(describeAdyenDocumentReason(reason));
    }
  },
  {
    condition: (params, { getState }) =>
      shouldFetchAdyenDocumentState(
        (getState() as RootState).businesses,
        params
      ),
  }
);
export const getBusinessOnboardingThunk = createAsyncThunk(
  "businesses/onboarding",
  async (params: { externalOnboardingId: string }) => {
    const response = await getBusinessOnboardingFromServer(params);
    return response;
  }
);
export const getBusinessUsersThunk = createAsyncThunk(
  "businesses/getBusinessUsers",
  async (businessId: string) => {
    const response = await getBusinessUsersFromServer(businessId);
    return response.users;
  }
);

export const completeRegistrationThunk = createAsyncThunk(
  "businesses/completeRegistration",
  async ({
    businessId,
    isExternalOnboarding,
  }: {
    businessId: string;
    isExternalOnboarding: boolean;
  }) => {
    const response = isExternalOnboarding
      ? await completeRegistrationExternalOnboardingServer({ businessId })
      : await completeRegistrationServer({ businessId });
    return response;
  }
);
export const deleteAndArchiveBusinessThunk = createAsyncThunk(
  "businesses/deleteAndArchive",
  async (props: { _id: string }) => {
    const response = await deleteAndArchiveBusinessServer(props);
    return { _id: props._id };
  }
);
export const deactivateBusinessThunk = createAsyncThunk(
  "businesses/deactivate",
  async (props: { _id: string; reason: string }) => {
    const response = await deactivateBusinessServer(props);
    return response;
  }
);
export const revertSubmissionBusinessThunk = createAsyncThunk(
  "businesses/revertSubmission",
  async (props: { _id: string }) => {
    const response = await revertSubmissionBusinessServer(props);
    return response;
  }
);
export const approveBusinessThunk = createAsyncThunk(
  "businesses/approve",
  async (props: { _id: string; email: string }) => {
    const response = await approveBusinessServer(props);
    return response;
  }
);

export const declineBusinessThunk = createAsyncThunk(
  "businesses/decline",
  async (props: { _id: string; email: string }) => {
    const response = await declineBusinessServer(props);
    return response;
  }
);
export const markAsPendingVerificationThunk = createAsyncThunk(
  "businesses/updateStatus",
  async (props: { _id: string }) => {
    const response = await markBusinessAsPendingVerificationServer(props);
    return response;
  }
);

export const editBusinessThunk = createAsyncThunk(
  "businesses/edit",
  async (
    props: { _id: string; updatedFields: Partial<IBusinessFull> },
    { rejectWithValue }
  ) => {
    try {
      const response = await editBusinessServer(props);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message }
      );
    }
  }
);
export const editBusinessUserThunk = createAsyncThunk(
  "businesses/editUser",
  async (props: { _id: string; updatedFields: Partial<IBusinessUser> }) => {
    const response = await editBusinessUserServer(props);
    return response;
  }
);
export const editBusinessUsersEmailThunk = createAsyncThunk(
  "businesses/editUserEmail",
  async (props: {
    _id: string;
    newEmail: string;
    oldEmail: string;
    sendCompleteOnboardingEmail?: boolean;
  }) => {
    const response = await editBusinessUsersEmailServer(props);
    return response;
  }
);
//utils//
const applyFiltersToBusinesses = (
  businesses: IBusinessFull[],
  filters: {
    [key: string]: ((businesses: IBusinessFull[]) => IBusinessFull[]) | null;
  }
) => {
  const filterArr = Object.values(filters);
  const filteredBusinesses = filterArr.reduce((prev, cur) => {
    if (prev.length === 0) return [];
    if (cur) return cur(prev);
    else return prev;
  }, businesses);

  return filteredBusinesses;
};

const partnerIdOf = (
  partnerId: IBusinessFull["partnerId"]
): string | undefined => {
  if (!partnerId) return undefined;
  if (typeof partnerId === "string") return partnerId;
  return (partnerId as { _id?: string })._id;
};

// Keeps a populated partner object when the saved id is already on the row.
const mergeSavedPricing = (
  business: IBusinessFull,
  pricing: UpdateBusinessPricingPayload
): IBusinessFull => {
  const next: IBusinessFull = { ...business };
  if (pricing.onlinePricing !== undefined) {
    next.onlinePricing = pricing.onlinePricing;
  }
  if (pricing.merchantServiceChargeFees !== undefined) {
    next.merchantServiceChargeFees = pricing.merchantServiceChargeFees;
  }
  if (pricing.onlineMerchantServiceChargeFees !== undefined) {
    next.onlineMerchantServiceChargeFees =
      pricing.onlineMerchantServiceChargeFees;
  }
  if (typeof pricing.partnerPurchPriceProfileId === "string") {
    next.partnerPurchPriceProfileId = pricing.partnerPurchPriceProfileId;
  }
  if (pricing.partnerOnlinePurchPriceProfileId !== undefined) {
    next.partnerOnlinePurchPriceProfileId =
      pricing.partnerOnlinePurchPriceProfileId;
  }
  if (
    pricing.partnerId !== undefined &&
    partnerIdOf(business.partnerId) !== pricing.partnerId
  ) {
    next.partnerId = pricing.partnerId ?? undefined;
  }
  return next;
};

export const businessesSlice = createSlice({
  name: "businesses",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // filterTableByStatus: (
    //   state,
    //   action: PayloadAction<"all" | BusinessStatus>
    // ) => {
    //   const status = action.payload;
    //   const statusFilter = (businesses: IBusinessFull[]) => {
    //     return businesses.filter((b) => b.status === status);
    //   };

    //   state.filters.status = status === "all" ? null : statusFilter;
    //   state.businessesInTable = applyFiltersToBusinesses(
    //     state.businesses,
    //     state.filters
    //   );
    // },
    // filterTableByType: (state, action: PayloadAction<"all" | BusinessType>) => {
    //   const businessType = action.payload;

    //   const typeFilter = (businesses: IBusinessFull[]) => {
    //     return businesses.filter((b) => b.businessType === businessType);
    //   };

    //   state.filters.type = businessType === "all" ? null : typeFilter;
    //   state.businessesInTable = applyFiltersToBusinesses(
    //     state.businesses,
    //     state.filters
    //   );
    // },

    // filterTableByName: (state, action: PayloadAction<string>) => {
    //   const name = action.payload;

    //   const nameFilter = (businesses: IBusinessFull[]) => {
    //     return businesses.filter(
    //       (b) =>
    //         b.businessName.toLowerCase().includes(name) ||
    //         b.rootUser?.email.toLowerCase().includes(name) ||
    //         b.rootUser?.name.toLowerCase().includes(name) ||
    //         b.businessRegistrationNumber.startsWith(name)
    //     );
    //   };

    //   state.filters.name = name === "" ? null : nameFilter;
    //   state.businessesInTable = applyFiltersToBusinesses(
    //     state.businesses,
    //     state.filters
    //   );
    // },
    setCurrentBusiness: (state, action: PayloadAction<IBusinessFull>) => {
      state.currentBusiness = action.payload;
    },
    // Merchant getBusinessDetails omits pricing, so a save must not reseed from it.
    applySavedBusinessPricing: (
      state,
      action: PayloadAction<{
        businessId: string;
        pricing: UpdateBusinessPricingPayload;
      }>
    ) => {
      const apply = (business: IBusinessFull) =>
        mergeSavedPricing(business, action.payload.pricing);
      if (state.currentBusiness?._id === action.payload.businessId) {
        state.currentBusiness = apply(state.currentBusiness);
      }
      state.businesses = state.businesses.map((business) =>
        business._id === action.payload.businessId ? apply(business) : business
      );
      state.lastUpdateKey = getUniqueKey();
    },
    setCurrentBusinessUser: (state, action: PayloadAction<IBusinessUser>) => {
      state.currentBusinessUser = action.payload;
    },
    resetBusinessOnboarding: (state) => {
      state.currentBusinessOnboarding = {} as any;
      state.isLoadingBusinessOnboarding = false;
    },
    setFilters: (state, action: PayloadAction<Partial<IBusinessFullQuery>>) => {
      let filters = { ...state.filters, ...action.payload };
      for (const key in filters) {
        //@ts-ignore
        if (!filters[key]) {
          //@ts-ignore
          delete filters[key];
        }
      }
      state.filters = filters;
    },
    resetFilters: (state) => {
      let filters = { ...state.filters };
      for (const key in filters) {
        //@ts-ignore
        if (filters[key]) {
          //@ts-ignore
          delete filters[key];
        }
      }
      state.filters = filters;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getBusinessesThunk.fulfilled, (state, action) => {
        state.businesses = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.isLoadingBusinesses = false;
      })
      .addCase(getBusinessesThunk.pending, (state, action) => {
        state.isLoadingBusinesses = true;
      })
      .addCase(getBusinessesThunk.rejected, (state, action) => {
        state.isLoadingBusinesses = false;
      })
      // The pricing pane persists its save through this refresh, so it has to
      // mark the list stale like every other save — otherwise the table keeps
      // the pre-save row and the editor reopens on it.
      .addCase(getBusinessThunk.fulfilled, (state, action) => {
        state.currentBusiness = action.payload;
        // Merged, not replaced: the detail payload has no list-computed fields.
        state.businesses = state.businesses.map((b) =>
          b._id === action.payload._id ? { ...b, ...action.payload } : b
        );
        state.lastUpdateKey = getUniqueKey();
      })
      .addCase(getFiscalizationConfigurationThunk.pending, (state) => {
        state.isLoadingPosRTConfigs = true;
      })
      .addCase(
        getFiscalizationConfigurationThunk.fulfilled,
        (state, action) => {
          state.posRTConfigs = action.payload;
          state.isLoadingPosRTConfigs = false;
        }
      )
      .addCase(getFiscalizationConfigurationThunk.rejected, (state) => {
        state.isLoadingPosRTConfigs = false;
      })
      .addCase(getBusinessUsersThunk.pending, (state, action) => {
        state.isLoadingBusinessUsers = true;
      })
      .addCase(getBusinessUsersThunk.fulfilled, (state, action) => {
        state.currentBusinessUsers = action.payload;
        state.isLoadingBusinessUsers = false;
      })
      .addCase(getBusinessUsersThunk.rejected, (state, action) => {
        state.isLoadingBusinessUsers = false;
      })
      .addCase(getBusinessFilesThunk.pending, (state, action) => {
        if (!action.meta.arg.silent) state.isLoadingBusinessFiles = true;
      })
      .addCase(getBusinessFilesThunk.fulfilled, (state, action) => {
        state.currentBusinessFiles = action.payload;
        state.isLoadingBusinessFiles = false;
      })
      .addCase(getBusinessFilesThunk.rejected, (state, action) => {
        state.isLoadingBusinessFiles = false;
      })
      .addCase(getAdyenDocumentStateThunk.pending, (state, action) => {
        // Re-pointing the held business (or replacing an in-flight fetch) drops rows.
        if (
          state.adyenDocumentsBusinessId !== action.meta.arg.businessId ||
          state.isLoadingAdyenDocuments
        ) {
          state.currentBusinessAdyenDocuments = [];
        }
        state.isLoadingAdyenDocuments = true;
        state.adyenDocumentsError = null;
        state.adyenDocumentsBusinessId = action.meta.arg.businessId;
        state.adyenDocumentsRequestId = action.meta.requestId;
      })
      .addCase(getAdyenDocumentStateThunk.fulfilled, (state, action) => {
        if (
          !isAdyenDocumentResponseCurrent(
            state,
            action.meta.arg.businessId,
            action.meta.requestId
          )
        )
          return;
        state.currentBusinessAdyenDocuments = action.payload;
        state.isLoadingAdyenDocuments = false;
      })
      .addCase(getAdyenDocumentStateThunk.rejected, (state, action) => {
        if (
          !isAdyenDocumentResponseCurrent(
            state,
            action.meta.arg.businessId,
            action.meta.requestId
          )
        )
          return;
        state.currentBusinessAdyenDocuments = [];
        state.isLoadingAdyenDocuments = false;
        state.adyenDocumentsError = action.payload ?? null;
      })
      .addCase(getBusinessOnboardingThunk.pending, (state, action) => {
        state.isLoadingBusinessOnboarding = true;
      })
      .addCase(getBusinessOnboardingThunk.fulfilled, (state, action) => {
        state.currentBusinessOnboarding = action.payload;
        state.isLoadingBusinessOnboarding = false;
      })
      .addCase(getBusinessOnboardingThunk.rejected, (state, action) => {
        state.isLoadingBusinessOnboarding = false;
      })
      // .addCase(editBusinessThunk.fulfilled, (state, action) => {
      //   state.currentBusiness = action.payload;

      //   state.businesses = state.businesses.map((b) =>
      //     b._id === action.payload._id ? action.payload : b
      //   );
      //   state.businessesInTable = state.businesses.map((b) =>
      //     b._id === action.payload._id ? action.payload : b
      //   );
      // })
      .addCase(editBusinessUserThunk.fulfilled, (state, action) => {
        state.currentBusinessUsers = state.currentBusinessUsers.map((u) =>
          u._id == action.payload._id ? action.payload : u
        );
        state.lastUpdateKey = getUniqueKey();
      })
      .addCase(deleteAndArchiveBusinessThunk.fulfilled, (state, action) => {
        const businesses = state.businesses.filter(
          (b) => b._id !== action.payload._id
        );
        state.businesses = businesses;
        state.lastUpdateKey = getUniqueKey();
      })
      .addCase(deactivateBusinessThunk.fulfilled, (state, action) => {
        const businesses = state.businesses.map((b) =>
          b._id === action.payload._id ? action.payload : b
        );
        state.businesses = businesses;
        state.lastUpdateKey = getUniqueKey();
      })
      .addCase(editBusinessUsersEmailThunk.fulfilled, (state, action) => {
        state.currentBusinessUsers = state.currentBusinessUsers.map((u) =>
          u._id == action.payload._id ? action.payload : u
        );
        state.lastUpdateKey = getUniqueKey();
      })
      .addMatcher(
        isAnyOf(
          approveBusinessThunk.fulfilled,
          declineBusinessThunk.fulfilled,
          revertSubmissionBusinessThunk.fulfilled,
          editBusinessThunk.fulfilled,
          completeRegistrationThunk.fulfilled,
          markAsPendingVerificationThunk.fulfilled,
          deactivateBusinessThunk.fulfilled
        ),
        (state, action) => {
          const businesses = state.businesses.map((b) =>
            b._id === action.payload._id ? action.payload : b
          );
          state.businesses = businesses;
          state.currentBusiness = action.payload;
          // state.businessesInTable = applyFiltersToBusinesses(
          //   state.businesses,
          //   state.filters
          // );
          state.lastUpdateKey = getUniqueKey();
        }
      );
  },
});

// export const { getBusinesses } = businessesSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`

export const selectBusinesses = (state: RootState) =>
  state.businesses.businesses;
export const selectTotalBusinessCount = (state: RootState) =>
  state.businesses.totalCount;
export const selectBusinessesInTable = (state: RootState) =>
  state.businesses.businessesInTable;
export const selectIsLoadingBusinesses = (state: RootState) =>
  state.businesses.isLoadingBusinesses;
export const selectCurrentBusiness = (state: RootState) =>
  state.businesses.currentBusiness;
export const selectPosRTConfigs = (state: RootState) =>
  state.businesses.posRTConfigs;
export const selectIsLoadingPosRTConfigs = (state: RootState) =>
  state.businesses.isLoadingPosRTConfigs;
export const selectCurrentBusinessFiles = (state: RootState) =>
  state.businesses.currentBusinessFiles;
export const selectIsLoadingBusinessFiles = (state: RootState) =>
  state.businesses.isLoadingBusinessFiles;
export const selectCurrentBusinessAdyenDocuments = (state: RootState) =>
  state.businesses.currentBusinessAdyenDocuments;
export const selectAdyenDocumentsError = (state: RootState) =>
  state.businesses.adyenDocumentsError;
export const selectAdyenDocumentsBusinessId = (state: RootState) =>
  state.businesses.adyenDocumentsBusinessId;
export const selectCurrentBusinessOnboarding = (state: RootState) =>
  state.businesses.currentBusinessOnboarding;
export const selectIsLoadingBusinessOnboarding = (state: RootState) =>
  state.businesses.isLoadingBusinessOnboarding;
export const selectIsLoadingBusinessUsers = (state: RootState) =>
  state.businesses.isLoadingBusinessUsers;
export const selectCurrentBusinessUsers = (state: RootState) =>
  state.businesses.currentBusinessUsers;
export const selectCurrentBusinessUser = (state: RootState) =>
  state.businesses.currentBusinessUser;
export const selectLastUpdateKey = (state: RootState) =>
  state.businesses.lastUpdateKey;
export const selectFilters = (state: RootState) => state.businesses.filters;
// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd =
//   (amount: number): AppThunk =>
//   (dispatch, getState) => {
//     const currentValue = selectCount(getState());
//     if (currentValue % 2 === 1) {
//       dispatch(incrementByAmount(amount));
//     }
//   };

export const {
  applySavedBusinessPricing,
  setCurrentBusiness,
  setCurrentBusinessUser,
  setFilters,
  resetFilters,
  resetBusinessOnboarding,
} = businessesSlice.actions;

export default businessesSlice.reducer;
